import { FormInstance, message, TablePaginationConfig } from 'antd';
import produce from 'immer';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { create } from 'zustand';
import GetGroupedOrders from '~/@core/application/useCases/order/getGroupedOrders';
import GetOrders from '~/@core/application/useCases/order/getOrders';
import FilterOrder from '~/@core/domain/filters/order/FilterOrder';
import GfpCarts from '~/@core/domain/model/gfp/GfpCarts';
import Order from '~/@core/domain/model/order/Order';
import Stocks from '~/@core/domain/model/stock/Stocks';
import FormFilterGfpCartsModel, {
  MapFormToFilter,
} from '~/@core/domain/ui/forms/FormFilterGfpCartsModel';
import FormFilterOrderModel, {
  MapFormToFilterOrder,
} from '~/@core/domain/ui/forms/FormFilterOrderModel';
import {
  ExportCartColumns,
  exportCartToExcel,
  getAllCampaigns,
} from '~/@core/infrastructure/api/services/gfpService';
import BeneficentCampaignGfp from '~/domain/model/BeneficentCampaignGfp/BeneficentCampaignGfp';
import { IPagedGfpParams } from '~/domain/model/Gfp/GfpSevice';
import ProductGroupWithCategories from '~/domain/model/PriceReport/ProductGroupWithCategories';
import { IProductSimple } from '~/domain/model/PriceReport/ProductSimple';
import ProductCategory from '~/domain/model/ProductCategory';
import Site from '~/domain/model/site';
import { generateExcelFromBase64 } from '~/helpers/util/generateExcelFromBase64';
import { withListKeys } from '~/helpers/util/generateListKeys';
import {
  getTableKlokReservationGfp,
  IParamsTableKlokOrder,
} from '~/services/producer/KlokReservation/KlokReservationService';

export type ITableParams = {
  orderBy: string | null;
  sort: number | null;
  pagination: TablePaginationConfig;
};

export type ITable = {
  data: GfpCarts[];
  isLoading: boolean;
  isSuccessful: boolean;
  isError: boolean;
  errorMessage: string;
  orderBy?: string;
  tableParams: ITableParams;
};

export type ITableOrders = {
  data: Order[];
  isLoading: boolean;
  isSuccessful: boolean;
  isError: boolean;
  errorMessage: string;
  tableParams: ITableParams;
};

export type IKlokReservationTableData = {
  id: number;
  productId: number;
  productCode: string;
  productDescription: string;
  productQuality: string;
  natureOperationDescription: string;
  totalQuantity: number;
  packingId: number;
  packingCode: string;
  packingName: string;
  qualityObservationDescription1?: string;
  qualityObservationDescription2?: string;
  qualityObservationId1?: number;
  qualityObservationId2?: number;
  qualityObservationCode1?: string;
  qualityObservationCode2?: string;
  remainingQuantityPacking: number;
  unitQuantity: number;
  observation: string;
  quantity?: number;
  packingQuantity: number;
  uniqueId: number;
};

export type IKlokReservationTable = {
  data: IKlokReservationTableData[];
  isLoading: boolean;
  isSuccessful: boolean;
  isError: boolean;
  errorMessage: string;
  tableParams: ITableParams;
};

export type IActions = {
  setData: (fn: (state: IProductCatalog) => void) => void;
  handleChangeStocks: (value: any, property: string, index: number) => void;
  duplicateStock: (stockKey: number) => void;
  removeStock: (stockId: number) => void;
  // FIXME Removido para atender as necessidades dos clientes sem bugs para corrigir com mais calma
  // handleSearchCarts: () => Order[];
  handleSearchOrders: () => GfpCarts[];
};

export type ISelectors = {
  mappedFilterGroups: () => ProductGroupWithCategories[];
  isMainFiltersSuccessful: () => boolean;
  mappedFilterProduct: () => IProductSimple[];
  mappedSiteIds: () => number[];
  mappedFilters: () => IFilters;
  validProductIds: () => number[];
  validCategoryIds: () => number[];
  validGroupIds: () => number[];
  isDisabledExportButton: () => boolean;
  getFormFilter: () => IPagedGfpParams;
};

export type IFormFilter = {
  siteIds: number[] | null;
  categoryIds?: number[] | null;
  commercialGroupIds?: number[] | null;
  productIds?: number[] | null;
};

export type IFormFilterModalKlok = {
  siteId: number | null;
  deliveryDate?: number[] | null;
  commercialDate?: number[] | null;
};

export type IObservationModal = {
  isLoading: boolean;
  isSuccessful: boolean;
  isError: boolean;
  errorMessage: string;
  isOpen: boolean;
  text: string;
};

export type IObservationModalState = {
  isObservationModalVisible: boolean;
  rowLine: IKlokReservationTableData | null;
};

export type IFilters = {
  sites: {
    data: Site[];
    isLoading: boolean;
    isSuccessful: boolean;
    isError: boolean;
    errorMessage: string;
  };
  categories: {
    data: ProductCategory[];
    isLoading: boolean;
    isSuccessful: boolean;
    isError: boolean;
    errorMessage: string;
  };
  groups: {
    data: ProductGroupWithCategories[];
    isLoading: boolean;
    isSuccessful: boolean;
    isError: boolean;
    errorMessage: string;
  };
  products: {
    data: IProductSimple[];
    isLoading: boolean;
    isSuccessful: boolean;
    isError: boolean;
    errorMessage: string;
  };
  beneficentCampaigns: {
    data: BeneficentCampaignGfp[];
    isLoading: boolean;
    isSuccessful: boolean;
    isError: boolean;
    errorMessage: string;
  };
  formFilter: IFormFilter;
};

export type IProductCatalog = {
  table: ITable;
  tableOrders: ITableOrders;
  klokReservationTable: IKlokReservationTable;
  filters: IFilters;
  formFilters: IFilters;
  formFilterInstance: Partial<FormInstance<IFormFilter>> | null;
  isOpenRequestModal: boolean;
  observationModal: IObservationModal;
} & IActions &
  ISelectors;

export type IModalState = {
  isOpen: boolean;
  isLoading: boolean;
  isSuccessful: boolean;
  confirmationModalVisible: boolean;
  isBeneficentCampaignLoading: boolean;
  observationModal: IObservationModalState;
};

export const defaultColumnOrderBy = 'productLongDescription';

export const initialStateTable: ITable = {
  data: [],
  isLoading: false,
  isSuccessful: false,
  isError: false,
  errorMessage: '',
  orderBy: defaultColumnOrderBy,
  tableParams: {
    pagination: {
      current: 1,
      pageSize: 100,
      total: 0,
      showSizeChanger: true,
    },
    orderBy: null,
    sort: null,
  },
};

export const initialStateTableOrders: ITableOrders = {
  data: [],
  isLoading: false,
  isSuccessful: false,
  isError: false,
  errorMessage: '',
  tableParams: {
    pagination: {
      current: 1,
      pageSize: 100,
      total: 0,
      showSizeChanger: true,
    },
    orderBy: null,
    sort: null,
  },
};

export const initialStateKlokReservationTable: IKlokReservationTable = {
  data: [],
  isLoading: false,
  isSuccessful: false,
  isError: false,
  errorMessage: '',
  tableParams: {
    pagination: {
      current: 1,
      pageSize: 100,
      total: 0,
      showSizeChanger: true,
    },
    orderBy: null,
    sort: null,
  },
};

export const initialModalState: IModalState = {
  isOpen: false,
  isLoading: false,
  isSuccessful: false,
  isBeneficentCampaignLoading: false,
  confirmationModalVisible: false,
  observationModal: {
    isObservationModalVisible: false,
    rowLine: null,
  },
};

export const initialObservationModal: IObservationModal = {
  isLoading: false,
  isSuccessful: false,
  isError: false,
  errorMessage: '',
  isOpen: false,
  text: '',
};

export const initialStateFilters: IFilters = {
  sites: {
    data: [],
    isLoading: false,
    isSuccessful: false,
    isError: false,
    errorMessage: '',
  },
  categories: {
    data: [],
    isLoading: false,
    isSuccessful: false,
    isError: false,
    errorMessage: '',
  },
  groups: {
    data: [],
    isLoading: false,
    isSuccessful: false,
    isError: false,
    errorMessage: '',
  },
  products: {
    data: [],
    isLoading: false,
    isSuccessful: false,
    isError: false,
    errorMessage: '',
  },
  beneficentCampaigns: {
    data: [],
    isLoading: false,
    isSuccessful: false,
    isError: false,
    errorMessage: '',
  },
  formFilter: {
    siteIds: null,
  },
};

interface IGfpService {
  tableParams: ITableParams;
  klokReservationTable: IKlokReservationTable;
  setKlokReservationTable: (data: IKlokReservationTableData[]) => void;
  // FIXME Removido para atender as necessidades dos clientes sem bugs para corrigir com mais calma
  // handleSearchCarts: () => Promise<GfpCarts[]>;
  handleSearchOrders: () => Promise<Order[]>;
  stocks: Stocks[];
  klokReservation: IKlokReservationTableData[];
  setStocks: (stockValues: Stocks[]) => void;
  klokReservationsSelected: IKlokReservationTableData[];
  setKlokReservationsSelected: (selectedReservations: IKlokReservationTableData[]) => void;
  order: Order[];
  setOrder: (orderValues: Order[]) => void;
  stocksSelected: Stocks[];
  setStocksSelected: (stockSelectedValues: Stocks[]) => void;
  // #FIXME Remover any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleChangeStocks: (value: any, property: string, index: number) => void;
  duplicateStock: (stockKey: number) => void;
  removeStock: (stockKey: number) => void;
  removeKlokReservation: (reservationKey: number) => void;
  selectedRowKeys: React.Key[];
  setSelectedRowKeys: (keys: React.Key[]) => void;
  selectedGfpIds: React.Key[];
  setSelectedGfpIds: (updater: React.Key[] | ((prev: React.Key[]) => React.Key[])) => void;
  filters: FormFilterGfpCartsModel | null;
  filtersOrder: FormFilterOrderModel | null;
  setFilters: (filters: FormFilterGfpCartsModel) => void;
  setFiltersOrder: (filters: FormFilterOrderModel) => void;
  isGrouped: boolean;
  setIsGrouped: (grouped: boolean) => void;
  groupedOrdersData: any[];
  setGroupedOrdersData: (data: any[]) => void;
  groupedOrdersPagination: { pageSize: number; current: number; pageNumber: number; total: number };
  setGroupedOrdersPagination: (pagination: {
    pageSize: number;
    current: number;
    pageNumber: number;
    total?: number;
  }) => void;
  getGroupedOrders: (paginationOverride?: { pageNumber?: number; pageSize?: number }) => void;
  pagination: { pageSize: number; current: number; pageNumber: number };
  table: ITable;
  tableOrders: ITableOrders;
  setPagination: (pagination: { pageSize: number; current: number; pageNumber: number }) => void;
  formFilterInstance: Partial<FormInstance<IFormFilter>> | null;
  formFilterModalKlok: Partial<FormInstance<IFormFilterModalKlok>> | null;
  setFormInstance: (form: FormInstance<any>) => void;
  setData: (fn: (state: IProductCatalog) => void) => GfpCarts[] | void;
  setDataOrders: (fn: (state: IProductCatalog) => void) => Order[] | void;
  getFormFilter: () => FilterOrder;
  getPagedParams: (overrideParams?: { pageNumber?: number; pageSize?: number }) => FilterOrder;
  getOrders: (paginationOverride?: { pageNumber?: number; pageSize?: number }) => void;
  fetchKlokReservations: (
    filterParams: IParamsTableKlokOrder,
  ) => Promise<IKlokReservationTableData[]>;
  handleChangeKlokReservation: (value: any, property: any, index: number) => void;
  handleChangeKlokReservationTable: (value: any, property: any, index: number) => void;
  updateKlokCheckedList: (record: IKlokReservationTableData) => void;
  duplicateKlokReservation: (reservationId: number) => void;
  observationModal: IObservationModal;
  setObservationModal: (isOpen: boolean) => void;
  isBeneficentCampaignLoading: boolean;
  getAllCampaigns: (date: string) => void;
  formFilters: IFilters;
  exportCart: (columns: ExportCartColumns) => Promise<void>;
  isExporting: boolean;
}

const hasElements = <T>(_array: T[]): boolean => Array.isArray(_array) && _array.length > 0;

const useGfpStore = create<IGfpService>((set, get) => ({
  // States
  formFilterInstance: null,
  formFilterModalKlok: null,
  tableParams: initialStateTable.tableParams,
  tableOrders: initialStateTableOrders,
  table: initialStateTable,
  stocks: [],
  klokReservation: [],
  klokReservationTable: initialStateKlokReservationTable,
  formFilters: initialStateFilters,
  isBeneficentCampaignLoading: false,
  isGrouped: false,
  groupedOrdersData: [],
  groupedOrdersPagination: { pageSize: 100, current: 1, pageNumber: 1, total: 0 },
  setIsGrouped: (grouped: boolean) => set({ isGrouped: grouped }),
  setGroupedOrdersData: (data: any[]) => set({ groupedOrdersData: data }),
  setGroupedOrdersPagination: pagination =>
    set(state => ({
      groupedOrdersPagination: { ...state.groupedOrdersPagination, ...pagination },
    })),
  setKlokReservationTable: data =>
    set(state => ({
      klokReservationTable: {
        ...initialStateKlokReservationTable,
        data,
      },
    })),
  assembleMOdal: initialModalState,
  observationModal: initialObservationModal,
  setStocks: stockValues => set({ stocks: stockValues }),
  order: [],
  setOrder: orderValues => set({ order: orderValues }),
  stocksSelected: [],
  setStocksSelected: stockSelectedValues => set({ stocksSelected: stockSelectedValues }),
  selectedRowKeys: [],
  setSelectedRowKeys: keys => set({ selectedRowKeys: keys }),
  selectedGfpIds: [],
  setSelectedGfpIds: updater =>
    set(state => ({
      selectedGfpIds: typeof updater === 'function' ? updater(state.selectedGfpIds) : updater,
    })),
  filters: null,
  filtersOrder: null,
  setFilters: filters => set({ filters }),
  setFiltersOrder: filters => set({ filtersOrder: filters }),
  pagination: { pageSize: 100, current: 1, pageNumber: 1 },
  setPagination: pagination => set({ pagination }),
  klokReservationsSelected: [],

  // Setters
  setData: (fn: any) => set(produce(fn)),
  setDataOrders: (fn: any) => set(produce(fn)),
  setFormInstance: (form: any) => {
    set({ formFilterInstance: form });
  },
  setKlokReservationsSelected: selectedReservations =>
    set({ klokReservationsSelected: selectedReservations }),
  setObservationModal: (isOpen: boolean) => {
    const { setData } = get();

    setData(state => {
      state.observationModal.isOpen = isOpen;
    });
  },

  // Actions
  handleChangeStocks: (value, property, index) => {
    set(state => {
      const stockMaped = state.stocks.map((stockLoop, i) => {
        if (i === index) {
          return {
            ...stockLoop,
            [property]: value,
            changed: true,
          };
        }
        return stockLoop;
      });
      return { stocks: stockMaped };
    });
  },
  duplicateStock: stockKey => {
    const nextNumber = get().stocksSelected.length + 1;

    set(state => {
      const stockToDuplicate = state.stocksSelected.find(stock => stock.stockKey === stockKey);
      if (stockToDuplicate) {
        const newStock = {
          ...stockToDuplicate,
          stockKey: Number(`${stockToDuplicate.stockId}${nextNumber}`),
          uniqueId: uuidv4(),
          changed: true,
        };
        return {
          stocks: [...state.stocks, newStock],
          stocksSelected: [...state.stocksSelected, newStock],
        };
      }
      return state;
    });
  },
  duplicateKlokReservation: (reservationId: number) => {
    set(state => {
      const reservationToDuplicate = state.klokReservationsSelected.find(
        reservation => reservation.uniqueId === reservationId,
      );
      if (reservationToDuplicate) {
        const existingIds = [
          ...state.klokReservationsSelected.map(item => item.uniqueId),
          ...state.klokReservationTable.data.map(item => item.uniqueId),
        ];
        const newUniqueId = existingIds.length > 0 ? Math.max(...existingIds) + 1 : 1;

        const newReservation = {
          ...reservationToDuplicate,
          uniqueId: newUniqueId,
        };

        return {
          klokReservation: [...state.klokReservation, newReservation],
          klokReservationsSelected: [...state.klokReservationsSelected, newReservation],
          klokReservationTable: {
            ...state.klokReservationTable,
            data: [...state.klokReservationTable.data, newReservation],
          },
        };
      }
      return state;
    });
  },
  removeStock: stockKey =>
    set(state => {
      const updatedStocks = state.stocks.map(stock => {
        if (stock.stockId === Math.floor(stockKey / 10)) {
          return {
            ...stock,
            observationId1: '',
            observationId2: '',
            quantityPacking: '',
            quantityPerPacking: '',
            packingDescription: '',
            packingCode: '',
            packingId: null,
          };
        }
        return stock;
      });
      return {
        stocks: updatedStocks,
        stocksSelected: state.stocksSelected.filter(stock => stock.stockKey !== stockKey),
      };
    }),

  removeKlokReservation: reservationKey =>
    set(state => {
      const updatedReservations = state.klokReservationsSelected.filter(
        reservation => reservation.uniqueId !== reservationKey,
      );
      const updatedTableData = state.klokReservationTable.data.filter(
        reservation => reservation.uniqueId !== reservationKey,
      );

      return {
        klokReservationsSelected: updatedReservations,
        klokReservationTable: {
          ...state.klokReservationTable,
          data: updatedTableData,
        },
      };
    }),

  // FIXME Removido para atender as necessidades dos clientes sem bugs para corrigir com mais calma
  // handleSearchCarts: async (): Promise<GfpCarts[]> => {
  //   const { filters, pagination } = get();
  //   if (!filters) {
  //     console.error('Filters cannot be null');
  //     return [];
  //   }
  //   const filter = MapFormToFilter.toFilter(filters);
  //   set({ table: { ...initialStateTable, isLoading: true } });
  //   try {
  //     // const response = await new GetGfpCarts().execute(filter);
  //     set({
  //       table: {
  //         // data: response.results,
  //         isLoading: false,
  //         isSuccessful: true,
  //         tableParams: {
  //           pagination: {
  //             ...pagination
  //             // total: response.totalCount
  //           }
  //         },
  //         isError: false,
  //         errorMessage: ''
  //       }
  //     });
  //     // return response.results;
  //   } catch (error: any) {
  //     set({
  //       table: {
  //         ...initialStateTable,
  //         isLoading: false,
  //         isSuccessful: false,
  //         isError: true,
  //         errorMessage: error.message
  //       }
  //     });
  //     return [];
  //   }
  // },
  handleSearchOrders: async (): Promise<Order[]> => {
    const { filtersOrder, pagination } = get();
    if (!filtersOrder) {
      console.error('Filters cannot be null');
      return [];
    }
    const filter = MapFormToFilterOrder.toFilter(filtersOrder, pagination);

    set({ tableOrders: { ...initialStateTableOrders, isLoading: true } });
    try {
      const response = await new GetOrders().execute(filter);

      set({
        tableOrders: {
          data: response.results,
          isLoading: false,
          isSuccessful: true,
          tableParams: {
            pagination: {
              ...pagination,
              total: response.totalCount,
            },
            orderBy: null,
            sort: null,
          },
          isError: false,
          errorMessage: '',
        },
      });
      return response.results;
    } catch (error: any) {
      set({
        tableOrders: {
          ...initialStateTableOrders,
          isLoading: false,
          isSuccessful: false,
          isError: true,
          errorMessage: error.message,
        },
      });
      return [];
    }
  },
  getFormFilter: (): FilterOrder => {
    const filtersOrder = get().filtersOrder;

    if (!filtersOrder) {
      throw new Error('filtersOrder cannot be null');
    }

    const startDate = moment(filtersOrder.DeliveryDate?.[0]?.toDate()).format('YYYY-MM-DD');
    const endDate = moment(filtersOrder.DeliveryDate?.[1]?.toDate()).format('YYYY-MM-DD');

    const siteId = hasElements(filtersOrder.siteId as unknown as unknown[])
      ? filtersOrder.siteId
      : [];

    const categoryIds =
      filtersOrder.CategoryId && hasElements(filtersOrder.CategoryId as unknown as unknown[])
        ? filtersOrder.CategoryId
        : [];

    const groupIds =
      filtersOrder.GroupId && hasElements(filtersOrder.GroupId as unknown as unknown[])
        ? filtersOrder.GroupId
        : [];

    const productIds =
      filtersOrder.ProductId && hasElements(filtersOrder.ProductId as unknown as unknown[])
        ? filtersOrder.ProductId
        : [];
    const colorIds =
      filtersOrder.ColorId && hasElements(filtersOrder.ColorId as unknown as unknown[])
        ? filtersOrder.ColorId
        : [];

    const productRecipient =
      filtersOrder.ProductRecipient &&
      hasElements(filtersOrder.ProductRecipient as unknown as unknown[])
        ? filtersOrder.ProductRecipient
        : '';

    const qualityIds =
      filtersOrder.QualityId && hasElements(filtersOrder.QualityId as unknown as unknown[])
        ? filtersOrder.QualityId
        : [];

    const orderNumber =
      filtersOrder.OrderNumber && hasElements(filtersOrder.OrderNumber as unknown as unknown[])
        ? filtersOrder.OrderNumber
        : '';

    const expeditionIds =
      filtersOrder.ExpeditionId && hasElements(filtersOrder.ExpeditionId as unknown as unknown[])
        ? filtersOrder.ExpeditionId
        : [];

    const customerIds =
      filtersOrder.CustomerId && hasElements(filtersOrder.CustomerId as unknown as unknown[])
        ? filtersOrder.CustomerId
        : [];

    return {
      siteId,
      DeliveryDate: [moment(startDate, 'YYYY-MM-DD'), moment(endDate, 'YYYY-MM-DD')],
      CategoryId: categoryIds,
      GroupId: groupIds,
      ProductId: productIds,
      ColorId: colorIds,
      ProductRecipient: productRecipient,
      QualityId: qualityIds,
      OrderNumber: orderNumber,
      ExpeditionId: expeditionIds,
      CustomerId: customerIds,
    } as unknown as FilterOrder;
  },
  getPagedParams(overrideParams?: { pageNumber?: number; pageSize?: number }): FilterOrder {
    const { filtersOrder, tableOrders } = get();
    const tableParams = tableOrders.tableParams;

    if (!filtersOrder) {
      throw new Error('filtersOrder cannot be null');
    }

    const paginationParams = {
      pageNumber: overrideParams?.pageNumber ?? tableParams.pagination.current ?? 1,
      pageSize: overrideParams?.pageSize ?? tableParams.pagination.pageSize ?? 100,
    };

    const filter = MapFormToFilterOrder.toFilter(filtersOrder, paginationParams);

    return {
      ...filter,
      sort: tableParams.sort,
      orderBy: tableParams.orderBy,
    } as FilterOrder;
  },
  getOrders: async (paginationOverride?: { pageNumber?: number; pageSize?: number }) => {
    const { setData, getPagedParams } = get();

    try {
      setData(state => {
        state.tableOrders.isLoading = true;
        state.tableOrders.isSuccessful = false;
        state.tableOrders.isError = false;
      });

      const params = getPagedParams(paginationOverride);

      const response = await new GetOrders().execute(params);

      const resultsWithKey = withListKeys(response.results);

      setData(state => {
        state.tableOrders.data = resultsWithKey;
        state.tableOrders.isLoading = false;
        state.tableOrders.isSuccessful = true;
        state.tableOrders.isError = false;
        state.tableOrders.tableParams.pagination.total = response.totalCount;
      });
    } catch (error) {
      setData(state => {
        state.tableOrders.isLoading = false;
        state.tableOrders.isSuccessful = false;
        state.tableOrders.isError = true;
        state.tableOrders.errorMessage =
          error instanceof Error ? error.message : 'An unknown error occurred';
      });
    } finally {
      setData(state => {
        state.tableOrders.isLoading = false;
      });
    }
  },
  getGroupedOrders: async (paginationOverride?: { pageNumber?: number; pageSize?: number }) => {
    const {
      setData,
      filtersOrder,
      groupedOrdersPagination,
      setGroupedOrdersData,
      setGroupedOrdersPagination,
    } = get();

    if (!filtersOrder) {
      console.error('filtersOrder cannot be null');
      return;
    }

    try {
      setData(state => {
        state.tableOrders.isLoading = true;
        state.tableOrders.isSuccessful = false;
        state.tableOrders.isError = false;
      });

      const paginationParams = {
        pageNumber: paginationOverride?.pageNumber ?? groupedOrdersPagination.pageNumber,
        pageSize: paginationOverride?.pageSize ?? groupedOrdersPagination.pageSize,
      };

      const filter = MapFormToFilterOrder.toFilter(filtersOrder, paginationParams);

      const response = await new GetGroupedOrders().execute(filter);

      setGroupedOrdersData(response.results);
      setGroupedOrdersPagination({
        ...groupedOrdersPagination,
        current: paginationParams.pageNumber,
        pageNumber: paginationParams.pageNumber,
        pageSize: paginationParams.pageSize,
        total: response.totalCount,
      });

      setData(state => {
        state.tableOrders.isLoading = false;
        state.tableOrders.isSuccessful = true;
        state.tableOrders.isError = false;
        state.tableOrders.tableParams.pagination.total = response.totalCount;
      });
    } catch (error) {
      setData(state => {
        state.tableOrders.isLoading = false;
        state.tableOrders.isSuccessful = false;
        state.tableOrders.isError = true;
        state.tableOrders.errorMessage =
          error instanceof Error ? error.message : 'An unknown error occurred';
      });
    } finally {
      setData(state => {
        state.tableOrders.isLoading = false;
      });
    }
  },
  fetchKlokReservations: async (
    filterParams: IParamsTableKlokOrder,
  ): Promise<IKlokReservationTableData[]> => {
    const { setData, klokReservationsSelected } = get();

    try {
      setData(state => {
        state.klokReservationTable.isLoading = true;
        state.klokReservationTable.isSuccessful = false;
        state.klokReservationTable.isError = false;
      });

      const response = await getTableKlokReservationGfp(filterParams);
      const data = response?.data || { results: [], totalCount: 0 };

      const existingIds = klokReservationsSelected.map(item => item.uniqueId);
      let nextId = existingIds.length > 0 ? Math.max(...existingIds) + 1 : 1;

      const resultsWithKey: IKlokReservationTableData[] =
        data.results?.map((item, index) => ({
          ...item,
          key: index + 1,
          uniqueId: nextId++,
        })) ?? [];

      setData(state => {
        state.klokReservationTable.data = resultsWithKey;
        state.klokReservationTable.tableParams.pagination.total = data.totalCount;
        state.klokReservationTable.isSuccessful = true;
        state.klokReservationTable.isError = false;
        state.klokReservationTable.isLoading = false;
      });

      return Promise.resolve(resultsWithKey);
    } catch (error) {
      setData(state => {
        state.klokReservationTable.data = [];
        state.klokReservationTable.isSuccessful = false;
        state.klokReservationTable.isError = true;
        state.klokReservationTable.isLoading = false;
      });
      message.error('Não foi possível carregar a tabela.');
      return Promise.reject(error);
    } finally {
      setData(state => {
        state.klokReservationTable.isLoading = false;
      });
    }
  },
  handleChangeKlokReservation: (value, property, index) => {
    set(state => {
      const updatedData = state.klokReservationsSelected.map((item, i) => {
        if (i === index) {
          return {
            ...item,
            [property]: value,
          };
        }
        return item;
      });

      const totalPacking = updatedData.reduce(
        (acc, elem) => acc + Number(elem.packingQuantity || 0),
        0,
      );

      return {
        klokReservationsSelected: updatedData,
        totalPacking,
      };
    });
  },
  handleChangeKlokReservationTable: (value, property, index) => {
    set(state => {
      const updatedData = state.klokReservationTable.data.map((item, i) => {
        if (i === index) {
          return {
            ...item,
            [property]: value,
          };
        }
        return item;
      });

      return {
        klokReservationTable: {
          ...state.klokReservationTable,
          data: updatedData,
        },
      };
    });
  },
  updateKlokCheckedList: record => {
    set(state => {
      const updatedSelected = [...state.klokReservationsSelected];
      const existingIndex = updatedSelected.findIndex(item => item.id === record.id);

      if (existingIndex !== -1) {
        updatedSelected.splice(existingIndex, 1);
      } else {
        updatedSelected.push(record);
      }

      return { klokReservationsSelected: updatedSelected };
    });
  },
  getAllCampaigns: async (commercialDate: string) => {
    const { setData } = get();

    try {
      setData(state => {
        state.formFilters.beneficentCampaigns.isLoading = true;
        state.formFilters.beneficentCampaigns.isSuccessful = false;
        state.formFilters.beneficentCampaigns.isError = false;
      });

      const data = await getAllCampaigns(commercialDate);
      setData(state => {
        state.formFilters.beneficentCampaigns.data = data;
        state.formFilters.beneficentCampaigns.isSuccessful = true;
        state.formFilters.beneficentCampaigns.isError = false;
      });
      return data;
    } catch (error) {
      setData(state => {
        if (!state.formFilters) {
          state.formFilters = { ...initialStateFilters };
        }
        state.formFilters.beneficentCampaigns.data = [];
        state.formFilters.beneficentCampaigns.isSuccessful = false;
        state.formFilters.beneficentCampaigns.isError = true;
      });
      console.error(`Erro ao buscar campanhas: ${String(error)}`);
    }
  },
  isExporting: false,
  exportCart: async (columns: ExportCartColumns) => {
    const { formFilterInstance } = get();

    if (!formFilterInstance || !formFilterInstance.getFieldsValue) {
      message.error('Filtros não encontrados. Por favor, realize uma busca primeiro.');
      return;
    }

    set({ isExporting: true });

    try {
      const formValues = formFilterInstance.getFieldsValue() as unknown as FormFilterGfpCartsModel;

      const filterParams = MapFormToFilter.toFilter(formValues, {
        pageSize: 100,
        pageNumber: 1,
      });

      const params = {
        ...filterParams,
        columns,
      };

      const base64Data = await exportCartToExcel(params);

      generateExcelFromBase64(
        base64Data,
        `exportacao-gfp-${moment().format('YYYY-MM-DD-HHmmss')}.xlsx`,
      );

      message.success('Exportação realizada com sucesso!');
    } catch (error) {
      message.error('Erro ao exportar. Tente novamente.');
    } finally {
      set({ isExporting: false });
    }
  },
}));

export default useGfpStore;
